// Staff management — real operator administration over GET/POST/DELETE /api/users.
// The App shell keeps this screen behind the same admin-or-owner gate as Settings.
// #378: the prototype's Team layout — member rows + invite card, and a member detail page
// with Profile / Access / Password / Delete sections. Same users API underneath; there is
// no Activity section because no activity endpoint exists.
import React from "react";
import { Banner, Button, Card, FormField, Input, Select } from "@/ds";
import { cockpit } from "@/api";
import { cachedRead } from "@/api/read-cache";
import { useLiveInvalidation } from "@/live/live-provider";
import type { UserRow } from "@/api";
import { Icon, SectionLayout, SectionNav, fmtDate } from "@/lib";
import { useRoutedState } from "@/lib/routed-state";
import type { StaffTab } from "../router";
import { useT } from "../i18n";
import { TeamsSection } from "./screen-staff-teams";
import type { PushToast } from "@/notifications/toast-queue";
import { staffIdentity, staffInitials } from "./staff-identity";
import { StaffInvitationPanel } from "./staff-invitation-panel";
import "./staff-people.css";

export interface StaffScreenProps {
  pushToast: PushToast;
  /** The signed-in user id — marks the own row and warns before self-demotion. */
  currentUserId?: string;
  /** Account provisioning is reserved for the platform owner in an explicit tenant context. */
  canCreateUsers: boolean;
  /** #1228: which tab the URL asks for. Absent means the screen keeps it in its own state. */
  tab?: StaffTab;
  onTabChange?: (tab: StaffTab) => void;
}

function RoleChip({ role }: { role: string }) {
  const t = useT();
  return (
    <span className={role === "admin" || role === "owner" ? "rolechip rolechip--admin" : "rolechip"}>
      {t(`role.${role}`, undefined, role)}
    </span>
  );
}

export function StaffScreen({ pushToast, currentUserId, canCreateUsers, tab: routedTab, onTabChange }: StaffScreenProps) {
  const t = useT();
  // #1536: the people from the last visit stay on screen while they refresh.
  const [rows, setRows] = React.useState<UserRow[]>(() => cachedRead("listUsers") ?? []);
  const [loading, setLoading] = React.useState(() => cachedRead("listUsers") === undefined);
  const [error, setError] = React.useState<string | null>(null);
  const [memberId, setMemberId] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [tab, setTab] = useRoutedState<StaffTab>(routedTab, onTabChange, "people");
  const returnFocusToCreate = React.useRef(false);

  // Live refetches can overlap; only the newest response may paint.
  const sequence = React.useRef(0);
  const load = React.useCallback(() => {
    setError(null);
    const mine = ++sequence.current;
    return cockpit.listUsers()
      .then((next) => { if (mine === sequence.current) setRows(next); })
      .catch((e) => { if (mine === sequence.current) setError(e?.message || t("common.error")); })
      .finally(() => { if (mine === sequence.current) setLoading(false); });
  }, [t]);
  React.useEffect(() => { load(); }, [load]);

  const invalidations = useLiveInvalidation("staff");
  React.useEffect(() => {
    if (invalidations === 0) return;
    load();
  }, [invalidations, load]);

  React.useEffect(() => {
    if (creating || !returnFocusToCreate.current) return;
    returnFocusToCreate.current = false;
    if (typeof document !== "undefined") {
      document.getElementById("staff-create-action")?.focus();
    }
  }, [creating]);

  const collapseCreate = () => {
    returnFocusToCreate.current = true;
    setCreating(false);
  };

  const member = memberId ? rows.find((u) => u.id === memberId) ?? null : null;

  if (member) {
    return (
      <MemberDetail
        member={member}
        isSelf={member.id === currentUserId}
        pushToast={pushToast}
        onBack={() => setMemberId(null)}
        onChanged={() => { void load(); }}
        onDeleted={() => { setMemberId(null); void load(); }}
      />
    );
  }

  return (
    <div className="page page--management page--staff page--staff-list">
      <div className="page-head">
        <div>
          <h1>{t("staff.title")}</h1>
        </div>
      </div>

      {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}

      <div className="staff-workspace-tabs" role="tablist" aria-label={t("staff.title")}>
        {(["people", "teams"] as const).map((value, index) => (
          <Button
            key={value}
            id={`staff-tab-${value}`}
            role="tab"
            variant="ghost"
            aria-selected={tab === value}
            aria-controls={`staff-panel-${value}`}
            tabIndex={tab === value ? 0 : -1}
            onClick={() => setTab(value)}
            onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const next = event.key === "Home" ? "people" : event.key === "End" ? "teams" : index === 0 ? "teams" : "people";
              setTab(next);
              document.getElementById(`staff-tab-${next}`)?.focus();
            }}
          >
            {t(value === "people" ? "staff.peopleTab" : "teams.title")}
          </Button>
        ))}
      </div>

      <div id="staff-panel-people" role="tabpanel" aria-labelledby="staff-tab-people" hidden={tab !== "people"}>
          <section className="staff-people staff-list" aria-label={t("staff.operators")}>
            <div className="staff-people__head staff-list__head">
              <h2 className="team-list__count">{t("staff.peopleCount", { n: rows.length })}</h2>
              {canCreateUsers && (
                <Button
                  id="staff-create-action"
                  className="staff-create-action"
                  variant="primary"
                  aria-expanded={creating}
                  aria-controls="staff-create-panel"
                  iconLeft={<Icon name="plus" size={16} />}
                  onClick={() => setCreating(true)}
                >
                  {t("staff.createOperator")}
                </Button>
              )}
            </div>
            {loading && <p className="staff-people__state subtle" aria-busy="true">{t("common.loading")}</p>}
            <div className="staff-people__cards">
            {!loading && rows.map((u) => {
              const identity = staffIdentity(u);
              const managed = u.role !== "owner";
              const PersonCard = managed ? "button" : "div";
              return (
                <PersonCard
                  className="staff-person-card"
                  key={u.id}
                  type={managed ? "button" : undefined}
                  onClick={managed ? () => setMemberId(u.id) : undefined}
                >
                  <span className="team-row__avatar" aria-hidden="true">{staffInitials(u)}</span>
                  <span className="staff-person-card__main">
                    <span className="staff-person-card__identity">
                      <b>{identity.primary}</b>
                      <RoleChip role={u.role} />
                    </span>
                    <span className="staff-person-card__meta">
                      {identity.secondary && <span>{identity.secondary}</span>}
                      <span>{t("staff.colCreated")} {u.createdAt ? fmtDate(u.createdAt) : t("common.notSet")}</span>
                    </span>
                  </span>
                  {!managed ? (
                    <span className="staff-person-card__managed">
                      {t("staff.platformManaged")}
                    </span>
                  ) : (
                    <span className="staff-person-card__action">
                      {t("staff.manage")}
                      <Icon name="chevronRight" size={16} />
                    </span>
                  )}
                </PersonCard>
              );
            })}
            </div>
            {!loading && !rows.length && <p className="staff-people__state subtle">{t("staff.noOperators")}</p>}
            {canCreateUsers && creating && (
              <InvitePanel
                pushToast={pushToast}
                onCancel={collapseCreate}
                onCreated={(created) => {
                  // POST /users returns the inserted server row. Paint that confirmed fact before
                  // collapsing, then refresh for canonical ordering/createdAt without hiding the
                  // new person if the follow-up GET happens to fail.
                  setRows((current) => [
                    ...current.filter((row) => row.id !== created.id),
                    created,
                  ]);
                  collapseCreate();
                  void load();
                }}
              />
            )}
          </section>

          {/* #1298: how a business adds somebody to itself. Always present rather than behind the
              owner-only create button — for a tenant admin this is the only way there is, and a
              team with nobody in it needs the way in to be visible, not discovered. */}
          <section className="staff-invitations" aria-label={t("staffInvite.title")}>
            <StaffInvitationPanel pushToast={pushToast} />
          </section>
      </div>

      <div id="staff-panel-teams" role="tabpanel" aria-labelledby="staff-tab-teams" hidden={tab !== "teams"}>
        {tab === "teams" && <TeamsSection staff={rows} pushToast={pushToast} />}
      </div>
    </div>
  );
}

function InvitePanel({ pushToast, onCancel, onCreated }: {
  pushToast: StaffScreenProps["pushToast"];
  onCancel: () => void;
  onCreated: (created: UserRow) => void;
}) {
  const t = useT();
  const [name, setName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [pw, setPw] = React.useState("");
  const [role, setRole] = React.useState("operator");
  const [busy, setBusy] = React.useState(false);

  const clear = () => {
    setName("");
    setEmail("");
    setPw("");
    setRole("operator");
  };

  const create = () => {
    if (!name.trim() || !email.trim() || !pw) return;
    setBusy(true);
    cockpit.createUser(email.trim(), pw, role, name.trim())
      .then((created) => {
        pushToast({ tone: "success", title: t("staff.operatorCreated") });
        clear();
        setBusy(false);
        onCreated(created);
      })
      .catch((e: any) => {
        pushToast({ tone: "danger", title: t("common.error"), msg: e?.message });
        setBusy(false);
      });
  };

  return (
    <div id="staff-create-panel" className="staff-create-panel">
      <div className="neo-eyebrow" style={{ marginBottom: "var(--space-4)" }}>{t("staff.createOperator")}</div>
      <div className="stack-sm staff-create-panel__form">
        <FormField label={t("staff.colName")} htmlFor="staff-create-name" required>
          <Input
            id="staff-create-name"
            value={name}
            maxLength={80}
            autoComplete="name"
            autoFocus
            required
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          />
        </FormField>
        <FormField label={t("staff.colEmail")}>
          <Input
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            // #782: a neutral example, matching screen-tenants. The old one named one specific
            // customer's mail domain in a form every other business also fills in.
            placeholder="name@acme-catering.de"
          />
        </FormField>
        <FormField label={t("login.password")}>
          <Input type="password" value={pw} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPw(e.target.value)} />
        </FormField>
        <FormField label={t("staff.colRole")}>
          <Select
            value={role}
            options={[
              { value: "operator", label: t("staff.roleOptionOperator") },
              { value: "admin", label: t("staff.roleOptionAdmin") },
            ]}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setRole(e.target.value)}
          />
        </FormField>
        <div className="management-actions">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              clear();
              onCancel();
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={busy || !name.trim() || !email.trim() || !pw}
            iconLeft={<Icon name="plus" size={16} />}
            onClick={create}
          >
            {t("staff.create")}
          </Button>
        </div>
        <p className="subtle" style={{ margin: 0, fontSize: "var(--text-caption)" }}>{t("staff.inviteNote")}</p>
      </div>
    </div>
  );
}

// The role ladder mirrors screen-tenants-access: operator < admin < owner; only the first
// two are assignable here (the platform owner is not granted from this screen).
const ROLE_LADDER: Array<{ key: string; assignable: boolean }> = [
  { key: "operator", assignable: true },
  { key: "admin", assignable: true },
  { key: "owner", assignable: false },
];

type MemberSection = "profile" | "access" | "password" | "remove";

function MemberDetail({ member, isSelf, pushToast, onBack, onChanged, onDeleted }: {
  member: UserRow;
  isSelf: boolean;
  pushToast: StaffScreenProps["pushToast"];
  onBack: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const t = useT();
  const identity = staffIdentity(member);
  const [section, setSection] = React.useState<MemberSection>("profile");
  const [pickedRole, setPickedRole] = React.useState(member.role);
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [sectionError, setSectionError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setPickedRole(member.role);
    setNewPassword("");
    setConfirmDelete(false);
    setSectionError(null);
  }, [member.id, member.role, section]);

  const run = (p: Promise<unknown>, ok: string, after?: () => void) => {
    setBusy(true);
    setSectionError(null);
    p.then(() => {
      pushToast({ tone: "success", title: ok });
      (after ?? onChanged)();
    })
      .catch((e: any) => setSectionError(e?.message || t("common.error")))
      .finally(() => setBusy(false));
  };

  const sections = [
    { id: "profile", label: t("staff.section.profile") },
    { id: "access", label: t("staff.section.access") },
    { id: "password", label: t("staff.section.password") },
    { id: "remove", label: t("staff.section.remove"), danger: true },
  ];
  const titles: Record<MemberSection, string> = {
    profile: t("staff.section.profile"),
    access: t("staff.section.access"),
    password: t("staff.section.password"),
    remove: t("staff.section.remove"),
  };

  return (
    <div className="page page--management page--staff">
      <div className="detailhead">
        <button className="iconbtn" aria-label={t("common.back")} onClick={onBack}><Icon name="arrowLeft" size={16} /></button>
        <span className="team-row__avatar" aria-hidden="true">{staffInitials(member)}</span>
        <div className="detailhead__id">
          <div className="detailhead__row">
            <h1 className="detailhead__title">{identity.primary}</h1>
            <RoleChip role={member.role} />
            {isSelf && <span className="quiet-flag">{t("staff.you")}</span>}
          </div>
          {identity.secondary && <div className="detailhead__email">{identity.secondary}</div>}
          <div className="detailhead__meta neo-mono">
            {t("staff.colCreated")} {member.createdAt ? fmtDate(member.createdAt) : t("common.notSet")}
          </div>
        </div>
      </div>

      <SectionLayout
        active={section}
        title={titles[section]}
        nav={<SectionNav entries={sections} active={section} onSelect={(id) => setSection(id as MemberSection)} ariaLabel={t("staff.sectionsAria")} />}
      >
        {sectionError && <Banner tone="danger" title={t("common.error")}>{sectionError}</Banner>}

        {section === "profile" && (
          <Card>
            <div className="stack-xs reqventos-detail-pairs">
              {identity.secondary && <div className="neo-kv"><span className="neo-kv__k">{t("staff.colName")}</span><span className="neo-kv__v">{identity.primary}</span></div>}
              <div className="neo-kv"><span className="neo-kv__k">{t("staff.colEmail")}</span><span className="neo-kv__v">{member.email}</span></div>
              <div className="neo-kv"><span className="neo-kv__k">{t("staff.colRole")}</span><span className="neo-kv__v"><RoleChip role={member.role} /></span></div>
              <div className="neo-kv"><span className="neo-kv__k">{t("staff.colCreated")}</span><span className="neo-kv__v">{member.createdAt ? fmtDate(member.createdAt) : t("common.notSet")}</span></div>
            </div>
            <p className="subtle" style={{ margin: "var(--space-4) 0 0", fontSize: "var(--text-caption)" }}>{t("staff.profileNote")}</p>
          </Card>
        )}

        {section === "access" && (
          <>
            <p className="seclayout__hint">{t("staff.accessHint")}</p>
            <div className="rolepick">
              {ROLE_LADDER.map(({ key, assignable }) => {
                const on = pickedRole === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={["rolepick__option", on ? "on" : "", assignable ? "" : "rolepick__option--locked"].join(" ").trim()}
                    aria-pressed={on}
                    disabled={!assignable}
                    onClick={() => assignable && setPickedRole(key)}
                  >
                    <span className="rolepick__ring" aria-hidden="true"><span /></span>
                    <span className="rolepick__text">
                      <span className="rolepick__label">{t(`role.${key}`, undefined, key)}</span>
                      <span className="rolepick__sub">{t(`staff.roleSub.${key}`)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="management-actions" style={{ marginTop: "var(--space-3)" }}>
              <Button variant="primary" disabled={busy || pickedRole === member.role}
                onClick={() => run(cockpit.setUserRole(member.id, pickedRole), t("staff.roleChanged"))}>
                {t("staff.applyRole")}
              </Button>
            </div>
            {isSelf && <p className="warn-text" style={{ marginTop: "var(--space-4)", fontSize: "var(--text-sm)" }}>{t("staff.selfWarning")}</p>}
          </>
        )}

        {section === "password" && (
          <Card>
            <p className="subtle" style={{ marginTop: 0 }}>{t("staff.pwHint")}</p>
            <div className="stack-sm" style={{ maxWidth: 420 }}>
              <FormField label={t("staff.newPw")} required>
                <Input type="password" value={newPassword}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)} />
              </FormField>
              <div className="management-actions">
                <Button loading={busy} variant="primary" disabled={busy || !newPassword}
                  onClick={() => run(cockpit.setUserPassword(member.id, newPassword), t("staff.pwChanged"), () => setNewPassword(""))}>
                  {t("staff.savePw")}
                </Button>
              </div>
              <p className="subtle" style={{ margin: 0, fontSize: "var(--text-caption)" }}>{t("staff.pwNote")}</p>
            </div>
          </Card>
        )}

        {section === "remove" && (
          <div className="dangercard management-editor reqventos-staff-delete">
            <div className="dangercard__title"><Icon name="trash" size={16} />{t("staff.deleteOperator")}</div>
            <p style={{ margin: "0 0 6px" }}>{t("staff.deleteConfirm")}</p>
            <p className="neo-mono" style={{ margin: "0 0 14px", fontWeight: "var(--weight-semibold)" }}>{member.email}</p>
            <p className="subtle" style={{ margin: "0 0 20px", fontSize: "var(--text-sm)" }}>{t("staff.deleteIrreversible")}</p>
            {!confirmDelete ? (
              <Button variant="danger-quiet" iconLeft={<Icon name="trash" size={16} />} onClick={() => setConfirmDelete(true)}>{t("staff.deleteOperator")}</Button>
            ) : (
              <div className="management-actions">
                <Button variant="ghost" disabled={busy} onClick={() => setConfirmDelete(false)}>{t("common.cancel")}</Button>
                <Button loading={busy} variant="destructive" disabled={busy}
                  onClick={() => run(cockpit.deleteUser(member.id), t("staff.deleted"), onDeleted)}>
                  {t("staff.deleteFinal")}
                </Button>
              </div>
            )}
          </div>
        )}
      </SectionLayout>
    </div>
  );
}
